import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Download, 
  DollarSign, 
  Users,
  Clock,
  Calendar,
  Loader2,
  FileSpreadsheet,
  AlertTriangle,
  RefreshCw,
  Filter
} from 'lucide-react';

export default function ContractorPayrollPage() {
  const { api, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);

  // Default to current week
  const getWeekDates = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Monday
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
    
    return {
      start: startOfWeek.toISOString().split('T')[0],
      end: endOfWeek.toISOString().split('T')[0]
    };
  };

  const defaultDates = getWeekDates();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/contractor-rates/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [api]);

  const fetchPreview = useCallback(async () => {
    if (!startDate || !endDate) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/contractor-rates/contractor-preview?start=${startDate}&end=${endDate}`);
      setPreview(response.data);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Failed to load payroll preview');
    } finally {
      setLoading(false);
    }
  }, [api, startDate, endDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }

    setExporting(true);
    
    try {
      const response = await api.post(
        `/contractor-rates/export?start=${startDate}&end=${endDate}`,
        {},
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contractor_payroll_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payroll exported successfully');
    } catch (error) {
      console.error('Error exporting payroll:', error);
      toast.error('Failed to export payroll');
    } finally {
      setExporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="bg-[#18181b] border-[#3f3f46] p-8">
          <div className="text-center text-zinc-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Access Denied</p>
            <p className="text-sm mt-2">Admin/Manager privileges required</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contractor-payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            CONTRACTOR PAYROLL
          </h1>
          <p className="text-sm text-zinc-500">Preview and export contractor pay calculations</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-400 uppercase tracking-wider">Contractors</p>
                  <p className="text-2xl font-bold text-white">{summary.contractor_count}</p>
                </div>
                <Users className="w-8 h-8 text-blue-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-emerald-400 uppercase tracking-wider">Active Rates</p>
                  <p className="text-2xl font-bold text-white">{summary.active_rates}</p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-400/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-yellow-400 uppercase tracking-wider">Without Rates</p>
                  <p className="text-2xl font-bold text-white">{summary.officers_without_rates}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-yellow-400/50" />
              </div>
            </CardContent>
          </Card>

          {preview && (
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-purple-400 uppercase tracking-wider">Period Total</p>
                    <p className="text-2xl font-bold text-white">${preview.grand_total?.pay?.toLocaleString() || '0'}</p>
                  </div>
                  <Clock className="w-8 h-8 text-purple-400/50" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* V4.1G-2 — Payroll Readiness */}
      {preview && (
        <Card className={`border ${
          preview.payroll_ready
            ? 'bg-emerald-500/10 border-emerald-500/40'
            : 'bg-red-500/10 border-red-500/40'
        }`}>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                {preview.payroll_ready ? (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/20">
                    <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-500/20">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                )}

                <div>
                  <div className={`font-rajdhani font-bold tracking-wider ${
                    preview.payroll_ready
                      ? 'text-emerald-400'
                      : 'text-red-400'
                  }`}>
                    {preview.payroll_ready
                      ? 'PAYROLL READY'
                      : 'PAYROLL BLOCKED'}
                  </div>

                  <p className="text-sm text-zinc-400 mt-1">
                    {preview.payroll_ready
                      ? 'All contractor attendance records have valid payroll rates. This payroll can be exported.'
                      : `${preview.exception_count || preview.exceptions?.length || 0} payroll exception(s) must be resolved before export.`}
                  </p>
                </div>
              </div>

              <Badge className={
                preview.payroll_ready
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }>
                {preview.payroll_ready ? 'VALIDATED' : 'ACTION REQUIRED'}
              </Badge>
            </div>

            {!preview.payroll_ready && preview.exceptions?.length > 0 && (
              <div className="mt-4 border-t border-red-500/20 pt-4 space-y-2">
                <div className="text-xs font-rajdhani font-semibold uppercase tracking-wider text-red-400">
                  Payroll Exceptions
                </div>

                {preview.exceptions.map((exception, idx) => (
                  <div
                    key={idx}
                    className="bg-red-950/30 border border-red-500/20 rounded-sm p-3"
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-red-300">
                        {exception.type === 'missing_contractor_rate'
                          ? 'Missing Contractor Rate'
                          : exception.type || 'Payroll Exception'}
                      </span>
                    </div>

                    <div className="text-xs text-zinc-400 mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                      {exception.officer_name && (
                        <span>Contractor: {exception.officer_name}</span>
                      )}

                      {exception.site_name && (
                        <span>Site: {exception.site_name}</span>
                      )}

                      {(exception.date || exception.work_date) && (
                        <span>Date: {exception.date || exception.work_date}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Date Range & Export */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-end gap-4">
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Start Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-[#09090b] border-[#3f3f46] pl-10"
                    data-testid="payroll-start-date"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">End Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-[#09090b] border-[#3f3f46] pl-10"
                    data-testid="payroll-end-date"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={fetchPreview}
                disabled={loading}
                className="border-zinc-600"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              <Button
                onClick={handleExport}
                disabled={
                    exporting ||
                    !preview?.lines?.length ||
                    preview?.payroll_ready !== true
                  }
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="export-payroll-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Table */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="border-b border-[#3f3f46] py-3 px-4">
          <CardTitle className="text-sm font-rajdhani text-zinc-300 uppercase tracking-wider flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-blue-400" />
            Payroll Preview
            {preview?.grand_total && (
              <Badge className="ml-2 bg-blue-500/20 text-blue-400">
                {preview.grand_total.line_count} records • {preview.grand_total.hours} hours
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
              <p className="text-zinc-500 mt-2">Loading preview...</p>
            </div>
          ) : !preview?.lines?.length ? (
            <div className="p-8 text-center text-zinc-500">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No payroll records for this period.</p>
              <p className="text-sm mt-2">
                Ensure contractors have attendance records with completed status.
              </p>
            </div>
          ) : (
            <>
              {/* Line Items */}
              <div className="overflow-x-auto max-h-[400px]">
                <table className="w-full">
                  <thead className="sticky top-0 bg-[#27272a]">
                    <tr className="border-b border-[#3f3f46]">
                      <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Contractor</th>
                      <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Day</th>
                      <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Date</th>
                      <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Site</th>
                      <th className="text-right p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Hours</th>
                      <th className="text-right p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Rate</th>
                      <th className="text-right p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Total</th>
                      <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#3f3f46]">
                    {preview.lines.map((line, idx) => (
                      <tr 
                        key={idx} 
                        className="hover:bg-[#27272a]/50 transition-colors"
                        data-testid={`payroll-line-${idx}`}
                      >
                        <td className="p-3">
                          <span className="text-sm text-zinc-200">{line.officer_name}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-zinc-400">{line.day}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-zinc-400 font-mono">{line.date}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-zinc-400">{line.site_name}</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-sm text-zinc-200 font-mono">{line.total_hours}</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-sm text-emerald-400 font-mono">${line.hourly_rate}</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-sm text-white font-bold font-mono">${line.total_pay}</span>
                        </td>
                        <td className="p-3">
                          <Badge className={`text-xs ${
                            line.rate_source === 'contract' ? 'bg-orange-500/20 text-orange-400' :
                            line.rate_source === 'site' ? 'bg-purple-500/20 text-purple-400' :
                            line.rate_source === 'default' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {line.rate_source}
                          </Badge>
                          {line.holiday_name && (
                            <Badge className="ml-1 text-xs bg-red-500/20 text-red-400">
                              {line.holiday_name}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals by Contractor */}
              <div className="border-t border-[#3f3f46] bg-[#27272a]/50">
                <div className="p-4">
                  <h3 className="text-sm font-rajdhani font-semibold text-zinc-400 uppercase mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Totals by Contractor
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {preview.by_officer?.map((officer, idx) => (
                      <div 
                        key={idx}
                        className="bg-[#18181b] rounded-sm p-3 border border-[#3f3f46]"
                        data-testid={`officer-total-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-300">{officer.officer_name}</span>
                          <span className="text-lg font-bold text-emerald-400">${officer.total_pay.toLocaleString()}</span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {officer.total_hours} hours • {officer.line_count} records
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grand Total */}
                <div className="border-t border-[#3f3f46] p-4 bg-gradient-to-r from-emerald-500/10 to-transparent">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-rajdhani font-semibold text-emerald-400 uppercase">Grand Total</span>
                      <div className="text-xs text-zinc-500">
                        {preview.grand_total.officer_count} contractors • {preview.grand_total.line_count} records • {preview.grand_total.hours} hours
                      </div>
                    </div>
                    <span className="text-3xl font-bold text-white">${preview.grand_total.pay.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
